<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";
import VDisclaimer from "@magnit/core/src/containers/VDisclaimer/VDisclaimer.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import VAppLink from "@magnit/core/src/components/VAppLink/VAppLink.vue";
import VHidden from "@magnit/core/src/components/VHidden/VHidden.vue";
import VQRCode from "@magnit/core/src/containers/VQRCode/VQRCode.vue";
import type { IProductDetailsInstallAppBannerProps } from "../types";

const props = withDefaults(
  defineProps<IProductDetailsInstallAppBannerProps>(),
  {
    bgColor: "#FFEBD3",
    utmSource: "magnit.ru",
  },
);
if (!props.bgColor.startsWith("#") || props.bgColor.length !== 7) {
  console.error(
    'The "bgColor" prop must start with "#" and be 7 characters long',
  );
}

const { query } = useRoute();

const disclaimerContent =
  "<a href='https://magnit.ru/akcii_uslovia_2' style='text-decoration: underline' target='_blank'>Условия акции</a>";
const promoLink = `https://promo.magnit.ru?utm_source=${props.utmSource}&utm_medium=pdp`;

const appDownloadUrl = computed(() => {
  try {
    const queryParams: Record<string, string> = {};
    const targetQuery = ["utm_medium", "utm_campaign", "utm_source", "erid"];
    targetQuery.forEach((s) => {
      if (query[s]) queryParams[s] = query[s] as string;
    });
    if (props.productId) queryParams["sku"] = props.productId;
    const queryString = new URLSearchParams(queryParams).toString();
    return queryString ? `${props.appUrl}?${queryString}` : props.appUrl;
  } catch {
    return "";
  }
});
// урлы картинок баннера в разных разрешениях
const { xs, s, m, ml, l, xl } = props.images;
</script>

<template>
  <div class="install-app-banner">
    <div class="install-app-banner__inner">
      <div class="install-app-banner__image">
        <VHidden :hide="{ xs: false, s: true }">
          <img
            :src="xs"
            alt="install app banner Magnit"
            class="install-app-banner__image--xs"
          />
        </VHidden>
        <VHidden :hide="{ xs: true, s: false, m: true }">
          <img
            :src="s || xs"
            alt="install app banner Magnit"
            class="install-app-banner__image--s"
          />
        </VHidden>
        <VHidden :hide="{ xs: true, s: true, m: false, ml: true }">
          <img
            :src="m || s || xs"
            alt="install app banner Magnit"
            class="install-app-banner__image--m"
          />
        </VHidden>
        <VHidden
          class="install-app-banner__image-wrap"
          :hide="{ xs: true, m: true, ml: false, l: true }"
        >
          <img
            :src="ml || m || s || xs"
            alt="install app banner Magnit"
            class="install-app-banner__image--ml"
          />
        </VHidden>
        <VHidden
          class="install-app-banner__image-wrap"
          :hide="{ xs: true, ml: true, l: false, xl: true }"
        >
          <img
            :src="l || ml || m || s || xs"
            alt="install app banner Magnit"
            class="install-app-banner__image--l"
          />
        </VHidden>
        <VHidden
          class="install-app-banner__image-wrap"
          :hide="{ xs: true, l: true, xl: false }"
        >
          <img
            :src="xl || l || ml || m || s || xs"
            alt="install app banner Magnit"
            class="install-app-banner__image--xl"
          />
        </VHidden>

        <VHidden
          :hide="{ xs: true, s: true, m: true, l: false }"
          class="install-app-banner__qr"
        >
          <div class="install-app-banner__qr-wrap">
            <VQRCode :width="116" :height="116" :value="appDownloadUrl" />
            <VText class="install-app-banner__qr-text" color="secondary">
              Сканируйте QR-код<br />и скачивайте приложение
            </VText>
          </div>
        </VHidden>
      </div>
      <div class="install-app-banner__text">
        <VText tag="h5" font="headline-small" class="install-app-banner__title"
          >Скидка 25% на&nbsp;первый заказ&nbsp;от&nbsp;1000&nbsp;₽</VText
        >
        <VText
          tag="div"
          :font="{ xs: 'body-large-regular', l: 'body-large-accent' }"
          class="install-app-banner__sub-title"
        >
          <span>Больше промокодов со&nbsp;скидками</span>
          <span
            >до&nbsp;50% на&nbsp;<a :href="promoLink" target="_blank"
              >promo.magnit.ru</a
            ><VDisclaimer :content="disclaimerContent"
          /></span>
        </VText>
      </div>
      <div class="install-app-banner__action">
        <VAppLink :to="appDownloadUrl">
          <VButton tag="span" class="install-app-banner__button"
            >Скачать приложение</VButton
          >
        </VAppLink>
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.install-app-banner {
  position: relative;

  @media (--pl-viewport-from-m) {
    padding-top: 30px;
  }

  @media (--pl-viewport-from-l) {
    padding-top: 50px;
  }

  &__inner {
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: var(--pl-unit-x4) 0;
    gap: var(--pl-unit-x5);
    background: v-bind(bgColor);
    border-radius: var(--pl-unit-x4);

    @media (--pl-viewport-from-l) {
      gap: var(--pl-unit-x3);
      padding: var(--pl-unit-x4);
      justify-content: space-between;
      position: relative;
    }

    @media (--pl-viewport-from-xl) {
      flex-direction: column-reverse;
      gap: var(--pl-unit-x5);
    }
  }

  &__image {
    @media (--pl-viewport-from-m) {
      position: absolute;
      top: 0;
      right: 0;
    }

    @media (--pl-viewport-from-ml) {
      top: unset;
      bottom: 0;
      max-width: 174px;
      max-height: 164px;
    }

    @media (--pl-viewport-from-l) {
      position: static;
      max-width: initial;
      max-height: initial;
    }

    &--m {
      max-width: 168px;
      max-height: 187px;
    }

    &--l {
      position: absolute;
      right: 0;
      top: -60px;
      max-width: 256px;
      max-height: 311px;
    }

    &--xl {
      position: absolute;
      right: 0;
      top: -50px;
      max-width: 297px;
      max-height: 292px;
    }

    img {
      width: 100%;
      vertical-align: bottom;
    }
  }

  &__qr-wrap {
    display: flex;
    flex-direction: column;
    gap: var(--pl-unit-x2);

    @media (--pl-viewport-from-xl) {
      flex-direction: row;
      gap: var(--pl-unit-x3);
    }

    :deep(div) {
      height: 100%;
    }

    :deep(img) {
      border-radius: var(--pl-unit-x2);
    }
  }

  &__qr-text {
    @media (--pl-viewport-from-xl) {
      align-self: flex-end;
    }
  }

  &__text {
    text-align: center;
    padding: 0 var(--pl-unit-x4);

    @media (--pl-viewport-from-m) {
      text-align: left;
      margin-right: 170px;
      margin-bottom: var(--pl-unit-x4);
      flex-direction: column;
      justify-content: space-around;
    }

    @media (width >= 729px) and (width <= 767px) {
      min-height: 116px;
    }

    @media (--pl-viewport-from-ml) {
      margin-bottom: 0;
    }

    @media (--pl-viewport-from-l) {
      margin-right: 0;
      padding: 0;
      gap: var(--pl-unit-x1);
      max-width: 330px;
      display: flex;
      flex-direction: column;
      justify-content: space-around;
    }
  }

  &__title {
    margin-bottom: var(--pl-unit-x2);
    white-space: pre-line;

    @media (--pl-viewport-from-m) {
      margin-bottom: 0;
    }

    @media (--pl-viewport-from-ml) {
      white-space: normal;
    }

    @media (--pl-viewport-from-l) {
      white-space: pre-line;
    }
  }

  &__sub-title {
    margin-top: var(--pl-unit-x1);
    display: flex;
    justify-content: center;
    flex-wrap: wrap;

    @media (--pl-viewport-from-m) {
      justify-content: flex-start;
    }

    span {
      &:nth-child(1) {
        flex-basis: 100%;

        @media (--pl-viewport-from-m) {
          flex-basis: auto;
        }
      }
    }

    a {
      color: var(--pl-text-link-default);
    }

    :deep(.unit-disclaimer) {
      margin-left: var(--pl-unit-x2);
      position: static;
      display: inline-block;

      @media (--pl-viewport-from-l) {
        position: absolute;
      }
    }
  }

  &__action {
    padding: 0 var(--pl-unit-x4);

    @media (--pl-viewport-from-ml) {
      margin-right: 180px;
    }

    @media (--pl-viewport-from-l) {
      display: none;
    }
  }

  &__button {
    width: 100%;

    @media (--pl-viewport-from-ml) {
      max-width: 200px;
    }
  }
}
</style>
